/**
 * Circuit breaker PartyKit por party (chat / status / sync).
 * SRP: disponibilidade e cooldown — sem criar sockets.
 */
import { isValidPartykitHost } from './partykitHost';

export type PartyKind = 'chat' | 'status' | 'sync';

const PARTYKIT_MAX_FAILURES = 3;
const PARTYKIT_COOLDOWN_MS = 30_000;

type PartyCircuit = { failures: number; disabledUntil: number };

const partyCircuits: Record<PartyKind, PartyCircuit> = {
    chat: { failures: 0, disabledUntil: 0 },
    status: { failures: 0, disabledUntil: 0 },
    sync: { failures: 0, disabledUntil: 0 },
};

function resetCircuit(circuit: PartyCircuit): void {
    circuit.failures = 0;
    circuit.disabledUntil = 0;
}

/** Circuit breaker por party — falha em `chat` não derruba `status`/`sync`. */
export function canUsePartykit(party?: PartyKind): boolean {
    if (!isValidPartykitHost()) return false;
    if (!party) return true;
    const circuit = partyCircuits[party];
    if (circuit.disabledUntil === 0) return true;
    if (Date.now() >= circuit.disabledUntil) {
        resetCircuit(circuit);
        return true;
    }
    return false;
}

export function reportPartykitFailure(party: PartyKind): void {
    const circuit = partyCircuits[party];
    circuit.failures += 1;
    if (circuit.failures >= PARTYKIT_MAX_FAILURES) {
        circuit.disabledUntil = Date.now() + PARTYKIT_COOLDOWN_MS;
        circuit.failures = 0;
    }
}

export function reportPartykitSuccess(party: PartyKind): void {
    resetCircuit(partyCircuits[party]);
}

export function createPartykitCooldownController(
    party: PartyKind,
    retry: () => void,
    isCancelled: () => boolean,
) {
    let cancelRetry = () => {};
    return {
        armAfterFailure() {
            cancelRetry();
            const remaining = partyCircuits[party].disabledUntil === 0
                ? 0
                : Math.max(0, partyCircuits[party].disabledUntil - Date.now());
            if (remaining === 0) {
                cancelRetry = () => {};
                return;
            }
            const timer = setTimeout(() => {
                if (!isCancelled() && canUsePartykit(party)) retry();
            }, remaining + 50);
            cancelRetry = () => clearTimeout(timer);
        },
        dispose() {
            cancelRetry();
            cancelRetry = () => {};
        },
    };
}
